/**
 * roomNaming — V14.96 : suggestions de noms de pièces en un clic.
 * Classe les candidats selon la surface (m²) et les pièces déjà nommées,
 * pour que l'étape « Meubler » reçoive un vrai programme (plus de « Pièce N »).
 */

export const GENERIC_ROOM_RE = /^\s*(pi[èe]ce|room|zone|espace)\s*\d*\s*$/i;
export const isGenericRoomLabel = (label) => !label || GENERIC_ROOM_RE.test(String(label));

// label affiché · type PlanEditor (ROOM_TYPES) · numérotable (Chambre 1, 2…)
export const ROOM_NAME_CATALOG = [
  { label: 'Séjour',          type: 'sejour' },
  { label: 'Salon',           type: 'sejour' },
  { label: 'Salle à manger',  type: 'sejour' },
  { label: 'Cuisine',         type: 'cuisine' },
  { label: 'Chambre',         type: 'chambre', numbered: true },
  { label: 'Suite parentale', type: 'chambre' },
  { label: 'Bureau',          type: 'chambre' },
  { label: 'Salle de bain',   type: 'sdb' },
  { label: "Salle d'eau",     type: 'sdb' },
  { label: 'WC',              type: 'wc', numbered: true },
  { label: 'Entrée',          type: 'entree' },
  { label: 'Dégagement',      type: 'couloir' },
  { label: 'Couloir',         type: 'couloir' },
  { label: 'Buanderie',       type: 'buanderie' },
  { label: 'Cellier',         type: 'buanderie' },
  { label: 'Dressing',        type: 'autre' },
  { label: 'Placard',         type: 'autre' },
  { label: 'Garage',          type: 'autre' },
  { label: 'Terrasse',        type: 'terrasse' },
  { label: 'Balcon',          type: 'balcon' },
  { label: 'Loggia',          type: 'loggia' },
  { label: 'Patio',           type: 'patio' },
  { label: 'Jardin',          type: 'jardin' },
];

const BY_AREA = [
  { max: 2.5,      order: ['WC', 'Placard', 'Dressing', 'Cellier', 'Dégagement', 'Entrée'] },
  { max: 6,        order: ["Salle d'eau", 'Salle de bain', 'WC', 'Entrée', 'Dégagement', 'Buanderie', 'Cellier', 'Dressing'] },
  { max: 9,        order: ['Salle de bain', 'Cuisine', 'Bureau', 'Chambre', 'Entrée', 'Buanderie', 'Couloir', "Salle d'eau"] },
  { max: 16,       order: ['Chambre', 'Cuisine', 'Bureau', 'Salle à manger', 'Suite parentale', 'Salle de bain', 'Couloir'] },
  { max: 30,       order: ['Séjour', 'Suite parentale', 'Cuisine', 'Salle à manger', 'Chambre', 'Garage', 'Terrasse'] },
  { max: Infinity, order: ['Séjour', 'Salon', 'Garage', 'Terrasse', 'Salle à manger', 'Suite parentale'] },
];
const DEFAULT_ORDER = ['Séjour', 'Cuisine', 'Chambre', 'Salle de bain', 'WC', 'Entrée', 'Couloir', 'Bureau', 'Buanderie', 'Dressing', 'Terrasse'];

/** Surface d'une pièce (polygone normalisé [x,y]) en m², ou null si non calibré. */
export const roomAreaM2 = (room, calibration, imgDims) => {
  const scale = calibration?.scale_m_per_norm_unit;
  const poly = room?.polygon;
  if (!scale || !poly || poly.length < 3) return null;
  let area = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    area += poly[j][0] * poly[i][1] - poly[i][0] * poly[j][1];
  }
  area = Math.abs(area) / 2;
  const iw = imgDims?.w || 1000;
  const ih = imgDims?.h || 1000;
  const mPerPx = scale / ((iw + ih) / 2);
  return area * iw * ih * mPerPx * mPerPx;
};

const baseOf = (label) => String(label || '').replace(/\s+\d+$/, '').trim().toLowerCase();

/**
 * Suggestions ordonnées pour `room`, en tenant compte des autres pièces.
 * → [{ label, type }] (label déjà numéroté si besoin : « Chambre 2 »).
 */
export const suggestRoomNames = (room, rooms, areaM2, limit = 7) => {
  const used = new Map(); // base → count
  (rooms || []).forEach((r) => {
    if (r.id === room?.id || isGenericRoomLabel(r.label)) return;
    const b = baseOf(r.label);
    used.set(b, (used.get(b) || 0) + 1);
  });
  const bucket = areaM2 == null ? null : BY_AREA.find((b) => areaM2 <= b.max);
  const order = [...(bucket ? bucket.order : DEFAULT_ORDER)];
  // Le type déjà détecté (IA) passe en tête si son libellé n'est pas encore utilisé.
  if (room?.type && room.type !== 'autre') {
    const typed = ROOM_NAME_CATALOG.find((c) => c.type === room.type);
    if (typed && !order.includes(typed.label)) order.unshift(typed.label);
    else if (typed) { order.splice(order.indexOf(typed.label), 1); order.unshift(typed.label); }
  }
  ROOM_NAME_CATALOG.forEach((c) => { if (!order.includes(c.label)) order.push(c.label); });

  const out = [];
  for (const label of order) {
    const cat = ROOM_NAME_CATALOG.find((c) => c.label === label);
    if (!cat) continue;
    const n = used.get(cat.label.toLowerCase()) || 0;
    if (n > 0 && !cat.numbered) continue;
    out.push({ label: cat.numbered && n > 0 ? `${cat.label} ${n + 1}` : cat.label, type: cat.type });
    if (out.length >= limit) break;
  }
  return out;
};

const pointInPolygon = (x, y, poly) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi) inside = !inside;
  }
  return inside;
};

/**
 * V14.97 — Zones ouvertes : pièces de part et d'autre d'un « mur invisible ».
 * → Map roomId → [labels des pièces voisines ouvertes]
 */
export const openZoneRooms = (walls, rooms) => {
  const out = new Map();
  const virt = (walls || []).filter((w) => w.type === 'mur_invisible');
  if (!virt.length || !(rooms || []).length) return out;
  const roomAt = (x, y) => rooms.find((r) => (r.polygon || []).length >= 3 && pointInPolygon(x, y, r.polygon));
  virt.forEach((w) => {
    const dx = w.x2 - w.x1; const dy = w.y2 - w.y1;
    const len = Math.hypot(dx, dy);
    if (len < 1e-6) return;
    const nx = -dy / len; const ny = dx / len; const eps = 0.01;
    const mx = (w.x1 + w.x2) / 2; const my = (w.y1 + w.y2) / 2;
    const a = roomAt(mx + nx * eps, my + ny * eps);
    const b = roomAt(mx - nx * eps, my - ny * eps);
    if (!a || !b || a.id === b.id) return;
    [[a, b], [b, a]].forEach(([r, o]) => {
      const list = out.get(r.id) || [];
      const lbl = o.label || o.id;
      if (!list.includes(lbl)) list.push(lbl);
      out.set(r.id, list);
    });
  });
  return out;
};

/** Résumé « programme » : Séjour · Cuisine · 2 Chambres · … */
export const roomProgramSummary = (rooms) => {
  const counts = new Map();
  (rooms || []).forEach((r) => {
    if (isGenericRoomLabel(r.label)) return;
    const b = baseOf(r.label);
    const display = String(r.label).replace(/\s+\d+$/, '').trim();
    const cur = counts.get(b) || { display, n: 0 };
    cur.n += 1;
    counts.set(b, cur);
  });
  return Array.from(counts.values()).map(({ display, n }) => (n > 1 ? `${n} ${display}s` : display));
};
